import { useCallback, useEffect, useMemo, useState } from "react";

/* ==============================
   CONFIG  (apne project ke hisaab se change karo)
============================== */
const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  "https://bmtvendorvendorfinal.onrender.com";

const BOOKINGS_URL = "/api/vendor/villas/bookings";

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("vendorToken") ||
  "";

/* 4 sections = backend ke 4 bookingStatus */
const TABS = [
  { key: "pending", label: "Incoming", empty: "No new booking requests right now." },
  { key: "confirmed", label: "Confirmed", empty: "No confirmed bookings yet." },
  { key: "completed", label: "Completed", empty: "No completed stays yet." },
  { key: "cancelled", label: "Cancelled", empty: "No cancelled bookings." },
];

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "-";

const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

async function api(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok || data.success === false) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

/* ==============================
   MAIN COMPONENT
   Usage: <VillaBookings />
============================== */
export default function VillaBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("pending");
  const [query, setQuery] = useState("");
  const [toast, setToast] = useState("");

  const [selected, setSelected] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const notify = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api(BOOKINGS_URL);
      setBookings(data.bookings || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { pending: 0, confirmed: 0, completed: 0, cancelled: 0 };
    bookings.forEach((b) => {
      if (c[b.bookingStatus] !== undefined) c[b.bookingStatus]++;
    });
    return c;
  }, [bookings]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings
      .filter((b) => b.bookingStatus === tab)
      .filter(
        (b) =>
          !q ||
          b.bookingReference?.toLowerCase().includes(q) ||
          b.guest?.name?.toLowerCase().includes(q) ||
          b.guest?.email?.toLowerCase().includes(q) ||
          b.guest?.phone?.includes(q)
      )
      .sort((a, b) =>
        tab === "pending" || tab === "confirmed"
          ? new Date(a.checkIn) - new Date(b.checkIn)
          : new Date(b.updatedAt) - new Date(a.updatedAt)
      );
  }, [bookings, tab, query]);

  const replaceBooking = (updated) => {
    setBookings((prev) => prev.map((b) => (b._id === updated._id ? updated : b)));
    setSelected((s) => (s && s._id === updated._id ? updated : s));
  };

  /* Confirm / Complete: backend mein PATCH /bookings/:id/status endpoint chahiye */
  const changeStatus = async (b, bookingStatus, okMsg) => {
    setBusy(true);
    try {
      const data = await api(`${BOOKINGS_URL}/${b._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ bookingStatus }),
      });
      replaceBooking(data.booking);
      setSelected(null);
      notify(okMsg);
    } catch (e) {
      notify(e.message);
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    setBusy(true);
    try {
      const data = await api(`${BOOKINGS_URL}/${cancelTarget._id}/cancel`, {
        method: "PATCH",
        body: JSON.stringify({ reason: reason.trim() || "Cancelled by vendor" }),
      });
      replaceBooking(data.booking);
      setCancelTarget(null);
      setSelected(null);
      setReason("");
      notify("Booking cancelled");
    } catch (e) {
      notify(e.message);
    } finally {
      setBusy(false);
    }
  };

  const activeTab = TABS.find((t) => t.key === tab);

  return (
    <div className="vb">
      <style>{css}</style>

      <header className="vb-head">
        <div>
          <h2>Villa bookings</h2>
          <p>Review new requests, track upcoming stays and see past bookings.</p>
        </div>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </header>

      <nav className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`tab ${tab === t.key ? "on" : ""} ${t.key}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            <span className="count">{counts[t.key]}</span>
          </button>
        ))}
      </nav>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search by guest, phone, email or reference"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading && <div className="state">Loading bookings...</div>}

      {!loading && error && (
        <div className="state error">
          <p>{error}</p>
          <button className="btn" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && visible.length === 0 && (
        <div className="state">
          {query ? "No bookings match your search." : activeTab.empty}
        </div>
      )}

      {!loading && !error && visible.length > 0 && (
        <div className="list">
          {visible.map((b) => (
            <article key={b._id} className="card" onClick={() => setSelected(b)}>
              <div className="card-main">
                <div className="who">
                  <strong>{b.guest?.name}</strong>
                  <span className="ref">{b.bookingReference}</span>
                </div>
                <div className="dates">
                  {fmtDate(b.checkIn)} to {fmtDate(b.checkOut)}
                  <small>
                    {b.nights} {b.nights === 1 ? "night" : "nights"},{" "}
                    {b.roomsBooked} {b.roomsBooked === 1 ? "room" : "rooms"}
                  </small>
                </div>
                <div className="guests">
                  {b.adults} adults{b.children > 0 ? `, ${b.children} children` : ""}
                  <small>{b.guest?.phone}</small>
                </div>
                <div className="amount">
                  {inr(b.totalAmount)}
                  <small className={`pay ${b.paymentStatus}`}>
                    {b.paymentMethod === "online" ? "Online" : "Pay at property"},{" "}
                    {b.paymentStatus}
                  </small>
                </div>
              </div>

              <div className="card-actions" onClick={(e) => e.stopPropagation()}>
                {b.bookingStatus === "pending" && (
                  <>
                    <button
                      className="btn primary sm"
                      disabled={busy}
                      onClick={() => changeStatus(b, "confirmed", "Booking confirmed")}
                    >
                      Confirm
                    </button>
                    <button className="btn danger sm" onClick={() => setCancelTarget(b)}>
                      Decline
                    </button>
                  </>
                )}
                {b.bookingStatus === "confirmed" && (
                  <>
                    <button
                      className="btn primary sm"
                      disabled={busy}
                      onClick={() => changeStatus(b, "completed", "Marked as completed")}
                    >
                      Mark completed
                    </button>
                    <button className="btn danger sm" onClick={() => setCancelTarget(b)}>
                      Cancel
                    </button>
                  </>
                )}
                {b.bookingStatus === "cancelled" && b.cancellationReason && (
                  <span className="reason">Reason: {b.cancellationReason}</span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      {/* ===== Detail drawer ===== */}
      {selected && (
        <div className="overlay side" onMouseDown={() => setSelected(null)}>
          <aside className="drawer" onMouseDown={(e) => e.stopPropagation()}>
            <div className="drawer-head">
              <div>
                <h3>{selected.guest?.name}</h3>
                <span className="ref">{selected.bookingReference}</span>
              </div>
              <span className={`pill ${selected.bookingStatus}`}>
                {TABS.find((t) => t.key === selected.bookingStatus)?.label}
              </span>
            </div>

            <Section title="Stay">
              <Row k="Check-in" v={fmtDate(selected.checkIn)} />
              <Row k="Check-out" v={fmtDate(selected.checkOut)} />
              <Row k="Nights" v={selected.nights} />
              <Row k="Rooms" v={selected.roomsBooked} />
              <Row
                k="Guests"
                v={`${selected.adults} adults${
                  selected.children ? `, ${selected.children} children` : ""
                }`}
              />
            </Section>

            <Section title="Guest">
              <Row k="Phone" v={<a href={`tel:${selected.guest?.phone}`}>{selected.guest?.phone}</a>} />
              <Row k="Email" v={<a href={`mailto:${selected.guest?.email}`}>{selected.guest?.email}</a>} />
            </Section>

            <Section title="Payment">
              <Row k="Rate per night" v={inr(selected.pricePerNight)} />
              <Row k="Subtotal" v={inr(selected.subtotal)} />
              <Row k="Taxes" v={inr(selected.taxes)} />
              <Row k="Total" v={<strong>{inr(selected.totalAmount)}</strong>} />
              <Row
                k="Method"
                v={selected.paymentMethod === "online" ? "Online" : "Pay at property"}
              />
              <Row k="Payment status" v={selected.paymentStatus} />
            </Section>

            {selected.specialRequests && (
              <Section title="Special requests">
                <p className="note">{selected.specialRequests}</p>
              </Section>
            )}

            {selected.bookingStatus === "cancelled" && (
              <Section title="Cancellation">
                <Row k="Cancelled on" v={fmtDateTime(selected.cancelledAt)} />
                <Row k="Reason" v={selected.cancellationReason || "-"} />
              </Section>
            )}

            <p className="meta">Booked on {fmtDateTime(selected.createdAt)}</p>

            <div className="drawer-actions">
              {selected.bookingStatus === "pending" && (
                <>
                  <button
                    className="btn primary"
                    disabled={busy}
                    onClick={() => changeStatus(selected, "confirmed", "Booking confirmed")}
                  >
                    Confirm booking
                  </button>
                  <button className="btn danger" onClick={() => setCancelTarget(selected)}>
                    Decline
                  </button>
                </>
              )}
              {selected.bookingStatus === "confirmed" && (
                <>
                  <button
                    className="btn primary"
                    disabled={busy}
                    onClick={() => changeStatus(selected, "completed", "Marked as completed")}
                  >
                    Mark completed
                  </button>
                  <button className="btn danger" onClick={() => setCancelTarget(selected)}>
                    Cancel booking
                  </button>
                </>
              )}
              <button className="btn" onClick={() => setSelected(null)}>
                Close
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ===== Cancel / decline modal ===== */}
      {cancelTarget && (
        <div className="overlay" onMouseDown={() => !busy && setCancelTarget(null)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <h3>
              {cancelTarget.bookingStatus === "pending"
                ? "Decline this booking?"
                : "Cancel this booking?"}
            </h3>
            <p>
              {cancelTarget.guest?.name}, {fmtDate(cancelTarget.checkIn)} to{" "}
              {fmtDate(cancelTarget.checkOut)}. This cannot be undone.
            </p>
            <label className="field">
              <span>Reason (shown on the booking)</span>
              <textarea
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Example: Villa under maintenance on these dates"
              />
            </label>
            <div className="modal-actions">
              <button className="btn" onClick={() => setCancelTarget(null)} disabled={busy}>
                Keep booking
              </button>
              <button className="btn danger-solid" onClick={confirmCancel} disabled={busy}>
                {busy ? "Cancelling..." : "Cancel booking"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="sec">
      <h4>{title}</h4>
      {children}
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div className="row">
      <span>{k}</span>
      <span>{v}</span>
    </div>
  );
}

/* ==============================
   STYLES
============================== */
const css = `
.vb{--ink:#18232f;--muted:#5f6c7a;--line:#dde3e9;--bg:#f4f6f8;--card:#fff;--accent:#0b6e6e;--accent-d:#095858;--danger:#b42318;--amber:#9a5b00;--blue:#1d4f91;--green:#157347;
  font-family:"Inter","Segoe UI",system-ui,sans-serif;color:var(--ink);background:var(--bg);padding:24px;border-radius:12px;max-width:1100px;margin:0 auto}
.vb *{box-sizing:border-box}
.vb-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;flex-wrap:wrap;margin-bottom:18px}
.vb-head h2{margin:0 0 4px;font-size:22px;font-weight:650}
.vb-head p{margin:0;color:var(--muted);font-size:14px}
.btn{border:1px solid var(--line);background:var(--card);color:var(--ink);padding:9px 16px;border-radius:8px;font-size:14px;font-weight:550;cursor:pointer}
.btn.sm{padding:6px 12px;font-size:13px}
.btn:hover:not(:disabled){border-color:#b9c3cd}
.btn.primary{background:var(--accent);border-color:var(--accent);color:#fff}
.btn.primary:hover:not(:disabled){background:var(--accent-d)}
.btn.danger{color:var(--danger);border-color:#f0c4c0}
.btn.danger-solid{background:var(--danger);border-color:var(--danger);color:#fff}
.btn:disabled{opacity:.6;cursor:not-allowed}
.btn:focus-visible,.tab:focus-visible,.vb input:focus-visible,.vb textarea:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.tabs{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:14px}
.tab{background:var(--card);border:1px solid var(--line);border-radius:10px;padding:14px 16px;font-size:14px;font-weight:600;color:var(--muted);cursor:pointer;display:flex;justify-content:space-between;align-items:center;border-bottom:3px solid transparent}
.tab .count{font-size:18px;font-weight:700;color:var(--ink)}
.tab.on{color:var(--ink);border-color:#b9c3cd}
.tab.pending.on{border-bottom-color:var(--amber)}
.tab.confirmed.on{border-bottom-color:var(--blue)}
.tab.completed.on{border-bottom-color:var(--green)}
.tab.cancelled.on{border-bottom-color:var(--danger)}
.toolbar{margin-bottom:14px}
.toolbar input{width:100%;max-width:420px;padding:10px 12px;border:1px solid var(--line);border-radius:8px;font-size:14px;font-family:inherit;background:#fff}
.state{background:var(--card);border:1px dashed var(--line);border-radius:10px;padding:40px 20px;text-align:center;color:var(--muted)}
.state p{margin:0 0 14px}
.state.error{color:var(--danger);border-color:#f0b8b3}
.list{display:flex;flex-direction:column;gap:10px}
.card{background:var(--card);border:1px solid var(--line);border-radius:10px;padding:14px 16px;cursor:pointer}
.card:hover{border-color:#b9c3cd}
.card-main{display:grid;grid-template-columns:1.3fr 1.3fr 1fr 1fr;gap:16px;align-items:start;font-size:14px}
.card small{display:block;color:var(--muted);font-size:12px;margin-top:3px}
.who strong{display:block;font-size:15px}
.ref{font-size:12px;color:var(--muted);font-family:ui-monospace,Consolas,monospace}
.amount{font-weight:650}
.pay{text-transform:capitalize}
.pay.paid{color:var(--green)}
.pay.failed{color:var(--danger)}
.card-actions{display:flex;gap:8px;align-items:center;margin-top:12px;padding-top:12px;border-top:1px solid var(--line)}
.card-actions:empty{display:none}
.reason{font-size:13px;color:var(--muted)}
.overlay{position:fixed;inset:0;background:rgba(24,35,47,.5);display:flex;align-items:center;justify-content:center;padding:16px;z-index:50}
.overlay.side{justify-content:flex-end;padding:0}
.drawer{background:var(--card);width:100%;max-width:440px;height:100%;overflow-y:auto;padding:24px}
.drawer-head{display:flex;justify-content:space-between;gap:12px;align-items:flex-start;margin-bottom:8px}
.drawer-head h3{margin:0 0 2px;font-size:19px}
.pill{font-size:12px;font-weight:650;padding:4px 10px;border-radius:999px;white-space:nowrap}
.pill.pending{background:#fdf1dc;color:var(--amber)}
.pill.confirmed{background:#e3edf9;color:var(--blue)}
.pill.completed{background:#e0f2e8;color:var(--green)}
.pill.cancelled{background:#fdecea;color:var(--danger)}
.sec{border-top:1px solid var(--line);padding:14px 0}
.sec h4{margin:0 0 8px;font-size:13px;color:var(--muted);font-weight:600}
.row{display:flex;justify-content:space-between;gap:12px;padding:4px 0;font-size:14px}
.row span:first-child{color:var(--muted)}
.row a{color:var(--accent);text-decoration:none}
.note{margin:0;font-size:14px;line-height:1.5}
.meta{font-size:12px;color:var(--muted);margin:4px 0 0}
.drawer-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:20px}
.modal{background:var(--card);border-radius:12px;padding:24px;width:100%;max-width:440px}
.modal h3{margin:0 0 8px;font-size:18px}
.modal p{color:var(--muted);font-size:14px;line-height:1.5;margin:0 0 14px}
.field span{display:block;font-size:13px;font-weight:600;margin-bottom:6px}
.field textarea{width:100%;padding:9px 11px;border:1px solid var(--line);border-radius:8px;font-size:14px;font-family:inherit;resize:vertical}
.modal-actions{display:flex;justify-content:flex-end;gap:10px;margin-top:18px}
.toast{position:fixed;bottom:24px;left:50%;transform:translateX(-50%);background:var(--ink);color:#fff;padding:10px 18px;border-radius:8px;font-size:14px;z-index:60;max-width:90vw}
@media (max-width:860px){
  .card-main{grid-template-columns:1fr 1fr}
}
@media (max-width:560px){
  .vb{padding:14px}
  .tabs{grid-template-columns:1fr 1fr}
  .card-main{grid-template-columns:1fr}
  .drawer{max-width:100%}
}
`;